import { Box, Stack } from '@mui/material';
import { GridRowIdGetter, GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { StyleObj } from '../../../@types';
import { ConditionEntity, ConditionEntityType } from '../../../@types/api';
import { CONDITION_ENTITY_CONFIG } from '../../../config/conditions';
import { QUERY_KEYS } from '../../../constants';
import useMutateData from '../../../hooks/useMutateData';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { ConditionFormData } from '../../../schema';
import ConditionsValuesForm from '../../organisms/forms/conditions/ConditionsValuesForm';
import TableTemplate from '../../templates/TableTemplate';
import { createIncludeSelectionModel } from '../../../utils/gridSelection';
import { defaultValues } from '../../organisms/forms/FilterPreMatchSettingsData';
import MobileDialogWrapper from '../../organisms/MobileDialogWrapper';

const makeStyles = (selectedRow: boolean): StyleObj => ({
  container: {
    display: 'grid',
    gridTemplateColumns: selectedRow ? '2fr 1fr' : '1fr',
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
    gap: 3,
  },
  createItemBtn: selectedRow
    ? {
        position: 'absolute',
        top: 8,
        right: 0,
        ml: 0,
      }
    : {},
  conditionsValuesFormWrapper: (theme) => ({
    mt: 11,
    backgroundColor: 'background.light',
    p: 3,
    borderRadius: 2,
    height: 'fit-content',
    [theme.breakpoints.down('md')]: {
      mt: 0,
      p: 2,
      height: '100vh',
      borderRadius: 0,
    },
  }),
});

export const URL_TO_CONDITION_MODEL_TYPE_MAP: Record<string, ConditionEntityType> = {
  sports: 'sport',
  competitions: 'competition',
  tournaments: 'tournament',
  markets: 'market_type',
  agents: 'agent',
  punters: 'punter',
};

const ConditionEntityPage = () => {
  const [selectedRow, setSelectedRow] = useState<{
    id: string;
    conditionGroupId: string | null;
    agentId?: string;
  } | null>(null);
  const location = useLocation();
  const pathSegment = location.pathname.split('/').pop();

  const type = pathSegment && URL_TO_CONDITION_MODEL_TYPE_MAP[pathSegment];

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<ConditionEntity>(
    'conditions',
    {
      page: 1,
      limit: 25,
      type,
    },
    {
      enabled: !!type,
    }
  );

  const { createData: createCondition } = useMutateData('conditions', [QUERY_KEYS.conditions]);

  const styles = makeStyles(Boolean(selectedRow));

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  useEffect(() => {
    changeQuery({ type, search: undefined });
    setSelectedRow(null);
  }, [changeQuery, type]);

  const { handleSort } = useSort(changeQuery);

  const { columns, defaultVisibleColumns } = CONDITION_ENTITY_CONFIG[type as keyof typeof CONDITION_ENTITY_CONFIG];

  const onFormCancel = () => {
    setSelectedRow(null);
  };

  const onSave = (data: ConditionFormData) => {
    createCondition(data);
  };

  const onRowClick = (row: GridRowParams<ConditionEntity>) => {
    setSelectedRow({
      id: row.row.id || row.row.userId,
      conditionGroupId: row.row.conditions?.conditionGroupId || null,
      agentId: row.row.agentId,
    });
  };

  const slotProps = {
    row: {
      style: {
        cursor: 'pointer',
      },
    },
  };

  useEffect(() => {
    changeQuery({ ...defaultValues, type: URL_TO_CONDITION_MODEL_TYPE_MAP[pathSegment as string] });
  }, [pathSegment, changeQuery]);

  const getRowId: GridRowIdGetter<ConditionEntity> = useCallback(
    (params) => {
      if (type === 'punter' || type === 'agent') {
        return params.userId;
      }

      return params.id;
    },
    [type]
  );

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={data?.items || []}
        rowCount={data?.count || 0}
        columns={columns}
        defaultVisibleColumns={defaultVisibleColumns}
        loading={isFetching || isLoading}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        changeQuery={changeQuery}
        onRowClick={onRowClick}
        rowSelectionModel={createIncludeSelectionModel(selectedRow ? [selectedRow.id] : [])}
        slotProps={slotProps}
        getRowId={getRowId}
      />
      {selectedRow && type && (
        <MobileDialogWrapper>
          <Stack spacing={2} sx={styles.conditionsValuesFormWrapper}>
            {type === 'punter' && (
              <ConditionsValuesForm
                onCancel={onFormCancel}
                onSave={onSave}
                conditionGroupId={null}
                conditionModelType='agent'
                modelId={selectedRow.agentId}
                preview
                formLabel='Agent Conditions'
              />
            )}
            <ConditionsValuesForm
              onCancel={onFormCancel}
              onSave={onSave}
              conditionGroupId={selectedRow?.conditionGroupId || null}
              conditionModelType={type}
              modelId={selectedRow.id}
            />
          </Stack>
        </MobileDialogWrapper>
      )}
    </Box>
  );
};

export default ConditionEntityPage;
